/**
 * Generation Recipe — the canonical contract for the Template Builder redesign.
 *
 * A recipe contains ONLY fields with a traced consumer in the Agent SDK
 * generation path (plan: docs/research/template-builder/APPROACH-kimi.md §1).
 *
 * Adjudication constraints baked into this type:
 * - R6: no `enhanceFormatting` — dead knob on the Agent SDK path
 *   (`agent_sdk_content_runner.py` deletes it from the bridge config).
 * - R7: no `includesFAQ` toggle — FAQ is forced `True` in the backend AUTO
 *   branch (`content_runner.py`: `includesFAQ=True`). Only `maxFAQItems`
 *   is exposed.
 * - Legacy hint fields `contentType`, `outlineStyle`, `stylePreset` are
 *   excluded (no Agent SDK reader).
 * - Canonical key spellings: `includesImages` / `includesList` /
 *   `includesCallouts` / `imageArtStyle` / `maxCalloutsPerArticle`.
 *
 * Field-set ceiling: `AutoTemplateFormData` (AutoTemplateConfigForm.tsx:23).
 * Fields the current form does NOT yet edit are marked with
 * `[not-in-form]` — they are declared optional so the serializer can
 * round-trip stored configs truthfully, and so a future form can adopt them
 * without a type change.
 */

export type RecipeTone = 'professional' | 'casual' | 'authoritative' | 'conversational';
export type RecipeListType = 'auto' | 'bullet' | 'numbered' | 'lettered';
export type RecipeImageArtStyle = 'photorealistic' | 'illustration' | '3d' | 'minimalist';
export type RecipeImagePosition = 'auto' | 'after-intro' | 'per-section' | 'end-only';
export type RecipeLinkStrategy = 'word-density' | 'auto' | 'fixed' | 'density-based';
export type RecipeH3Strategy = 'threshold' | 'all' | 'every-other';

export interface GenerationRecipe {
  // ---- identity (recipe metadata; written to the template row, not default_config) ----
  name: string;
  description: string;

  // ---- length & tone (top-level default_config keys; content_runner.py:3314-3315) ----
  targetWordCount: number;
  tone: RecipeTone;

  // ---- structure ----
  minSections?: number;
  maxSections?: number;
  /** [not-in-form] Read by final_assembler from the serialized config. */
  includesTableOfContents?: boolean;
  /** [not-in-form] H3 subsection policy (models.py:300-322). */
  includesH3Subsections?: boolean;
  /** [not-in-form] */
  h3Strategy?: RecipeH3Strategy;
  /** [not-in-form] */
  h3CountPerSection?: number;
  /** [not-in-form] */
  h3MinSectionWords?: number;

  // ---- components ----
  includesList: boolean;
  listType: RecipeListType;
  maxListItems?: number;
  /** FAQ itself is always-on (R7); this is its only exposed control. */
  maxFAQItems?: number;
  /** [not-in-form] */
  includesTables?: boolean;
  /** [not-in-form] */
  maxTablesPerArticle?: number;
  /** [not-in-form] */
  tableColumns?: number;
  /** [not-in-form] */
  tableRows?: number;
  /** [not-in-form] */
  includesCTA?: boolean;
  /** [not-in-form] */
  includesStatsGrid?: boolean;
  /** [not-in-form] */
  includesBlockquote?: boolean;
  /** [not-in-form] */
  includesCallouts?: boolean;
  /** [not-in-form] */
  calloutVariants?: string[];
  /** [not-in-form] */
  maxCalloutsPerArticle?: number;

  // ---- images ----
  includesImages: boolean;
  minImages?: number;
  maxImages?: number;
  /** Canonical spelling. The backend tolerates legacy `imageStyle` on read
   *  (content_runner.py:2547-2549 reads `imageStyle` first); the serializer
   *  writes ONLY `imageArtStyle`. */
  imageArtStyle: RecipeImageArtStyle;
  imagePosition: RecipeImagePosition;

  // ---- links ----
  linkStrategy: RecipeLinkStrategy;
  /** null/undefined = AI decides (backend treats null as AI-decides). */
  internalLinkCount?: number;
  externalLinkCount?: number;

  // ---- model selection (top-level default_config keys; content_runner.py:2683,2688) ----
  languageModel?: string;
  imageModel?: string;
}

/**
 * Default recipe values.
 *
 * IMPORTANT: these mirror the *effective* defaults of the live AUTO-branch
 * construction in content_runner.py (~:2512-2579), NOT the pydantic model
 * defaults in models.py — the runner passes explicit kwargs for every field,
 * so its `.get(...)` fallbacks are what actually executes. Divergences from
 * models.py (runner value used here): includesCTA false (model: true),
 * includesBlockquote true (model: false), includesCallouts true (model: false),
 * tableColumns 4 (model: 3), tableRows 5 (model: 4), linkStrategy 'auto'
 * (model/form: 'word-density'). See recipePayload.ts TSDoc.
 *
 * `languageModel` / `imageModel` are intentionally left undefined: the form
 * layer injects DEFAULT_LLM_MODEL / DEFAULT_IMAGE_MODEL at edit time.
 */
export const DEFAULT_RECIPE: GenerationRecipe = {
  name: '',
  description: '',
  targetWordCount: 1500,
  tone: 'professional',

  minSections: 3,
  maxSections: 8,
  includesTableOfContents: true,
  includesH3Subsections: true,
  h3Strategy: 'threshold',
  h3CountPerSection: 2,
  h3MinSectionWords: 150,

  includesList: true,
  listType: 'auto',
  maxListItems: 8,
  maxFAQItems: 5,
  includesTables: false,
  maxTablesPerArticle: 1,
  tableColumns: 4,
  tableRows: 5,
  includesCTA: false,
  includesStatsGrid: true,
  includesBlockquote: true,
  includesCallouts: true,
  calloutVariants: ['callout'],
  maxCalloutsPerArticle: 2,

  includesImages: true,
  minImages: 1,
  maxImages: 3,
  imageArtStyle: 'photorealistic',
  imagePosition: 'auto',

  linkStrategy: 'auto',
  internalLinkCount: undefined,
  externalLinkCount: undefined,

  languageModel: undefined,
  imageModel: undefined,
};
